import * as voicifyApi from '../../../../shared/api';
import { getHealthcareSchedulingModuleId } from '../../../../shared/constants/ModuleConsts';
import AppliedApplicationTemplateModel from '../../../../shared/models/templating/api/AppliedApplicationTemplateModel';

export interface PlatformSchedulingOrganizationTarget {
    id: string;
    name: string;
    applicationIds: string[];
    sharedConfigurationCandidates: Array<{ id: string; name: string }>;
}

export interface PlatformSchedulingOrganizationQueueResult {
    organizationId: string;
    organizationName: string;
    applicationCount: number;
    acceptedJobCount: number;
    errors: string[];
}

const getAppliedTemplateConfigurationId = (appliedTemplate: any): string | undefined =>
    appliedTemplate?.templateForm?.templateConfigurationId
    || appliedTemplate?.templateForm?.templateConfiguration?.id;

const getTemplateApplicationId = (appliedTemplate: any): string | undefined =>
    appliedTemplate?.templateForm?.templateConfiguration?.templateApplicationId;

const isOrganizationOwnedTemplate = (appliedTemplate: any, organizationId: string): boolean =>
    appliedTemplate?.templateForm?.templateConfiguration?.organizationId === organizationId;

const mapWithConcurrency = async <T, R>(items: T[], limit: number, mapper: (item: T) => Promise<R>): Promise<R[]> => {
    const results = new Array<R>(items.length);
    let nextIndex = 0;

    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (nextIndex < items.length) {
            const index = nextIndex++;
            results[index] = await mapper(items[index]);
        }
    });

    await Promise.all(workers);
    return results;
};

export const resolvePlatformSharedConfigurationId = async (
    candidates: Array<{ id: string; name: string }>,
    coreModuleId: string,
    moduleName: string
): Promise<{ id?: string; error?: string }> => {
    if (!coreModuleId) {
        return { error: `${moduleName} module is not configured for this environment.` };
    }

    const checks = await mapWithConcurrency(candidates, 5, async candidate => {
        try {
            const result = await voicifyApi.getAppliedApplicationTemplatesForApp(candidate.id);
            if (result.resultType !== 'Ok') {
                return { candidate, matches: false, error: true };
            }

            return {
                candidate,
                matches: (result.data || []).some(template =>
                    getAppliedTemplateConfigurationId(template) === coreModuleId
                ),
                error: false
            };
        } catch {
            return { candidate, matches: false, error: true };
        }
    });

    if (checks.some(check => check.error)) {
        return { error: 'Unable to verify every shared configuration candidate. Nothing was queued for this organization.' };
    }

    const matches = checks.filter(check => check.matches);
    if (matches.length === 0) {
        return { error: `No shared configuration with the ${moduleName} module was found. Nothing was queued.` };
    }
    if (matches.length > 1) {
        return { error: `Multiple ${moduleName} shared configurations were found. Nothing was queued.` };
    }

    return { id: matches[0].candidate.id };
};

export const resolveHealthcareSharedConfigurationId = (
    candidates: Array<{ id: string; name: string }>,
    healthcareSchedulingModuleId: string
): Promise<{ id?: string; error?: string }> =>
    resolvePlatformSharedConfigurationId(candidates, healthcareSchedulingModuleId, 'Healthcare Scheduling');

export const queuePlatformSchedulingSyncAndPublish = async (
    targets: PlatformSchedulingOrganizationTarget[],
    onResult?: (result: PlatformSchedulingOrganizationQueueResult) => void,
    coreModuleId = getHealthcareSchedulingModuleId(),
    moduleName = 'Healthcare Scheduling'
): Promise<PlatformSchedulingOrganizationQueueResult[]> => {
    const results: PlatformSchedulingOrganizationQueueResult[] = [];

    for (const target of targets) {
        const errors: string[] = [];
        let acceptedJobCount = 0;

        try {
            let sharedConfigurationCandidates = target.sharedConfigurationCandidates;
            if (sharedConfigurationCandidates.length === 0) {
                const applicationsResult = await voicifyApi.getApplicationsForOrg(target.id);
                if (applicationsResult.resultType !== 'Ok') {
                    throw new Error(`Unable to load ${moduleName} shared configuration metadata. Nothing was queued for this organization.`);
                } else {
                    sharedConfigurationCandidates = (applicationsResult.data || [])
                        .filter(application => application.allowsTemplating && application.id)
                        .map(application => ({ id: application.id!, name: application.name || 'Shared Configuration' }));
                }
            }

            const sharedConfiguration = await resolvePlatformSharedConfigurationId(
                sharedConfigurationCandidates,
                coreModuleId,
                moduleName
            );

            if (!sharedConfiguration.id) {
                errors.push(sharedConfiguration.error || `Unable to identify the ${moduleName} shared configuration.`);
            } else {
                const moduleChecks = await mapWithConcurrency(target.applicationIds, 5, async applicationId => {
                    try {
                        const result = await voicifyApi.getAppliedApplicationTemplatesForApp(applicationId);
                        if (result.resultType !== 'Ok') {
                            return { applicationId, templates: null as any[] | null };
                        }
                        return { applicationId, templates: result.data || [] };
                    } catch {
                        return { applicationId, templates: null as any[] | null };
                    }
                });

                if (moduleChecks.some(check => check.templates === null)) {
                    errors.push('Unable to verify the applied modules for every selected app. Nothing was queued for this organization.');
                } else {
                    const publishOnlyApplicationIds: string[] = [];
                    const sharedConfigurationApplicationIds: string[] = [];
                    const independentModuleTargets: Array<{ applicationId: string; appliedTemplateId: string }> = [];

                    moduleChecks.forEach(({ applicationId, templates }) => {
                        if (templates.length === 0) {
                            publishOnlyApplicationIds.push(applicationId);
                            return;
                        }

                        // Shared membership is explicit: the org-owned template must point to the verified shared app.
                        const hasSharedConfiguration = templates.some(template =>
                            isOrganizationOwnedTemplate(template, target.id)
                            && getTemplateApplicationId(template) === sharedConfiguration.id
                        );

                        if (hasSharedConfiguration) {
                            sharedConfigurationApplicationIds.push(applicationId);
                        } else {
                            const directModuleMatches = templates.filter(template =>
                                getAppliedTemplateConfigurationId(template) === coreModuleId
                            );
                            const independentModule = directModuleMatches.length === 1
                                ? directModuleMatches[0]
                                : templates.length === 1 ? templates[0] : undefined;

                            if (independentModule?.id) {
                                independentModuleTargets.push({ applicationId, appliedTemplateId: independentModule.id });
                            } else {
                                errors.push(`App ${applicationId} is not linked to the verified ${moduleName} shared configuration and its independent module could not be identified safely.`);
                            }
                        }
                    });

                    if (errors.length === 0) {
                        const independentModulePreflightResults = await mapWithConcurrency(independentModuleTargets, 5, async independentTarget => {
                            const [templateResult, environmentsResult] = await Promise.all([
                                voicifyApi.findAppliedApplicationTemplate(independentTarget.appliedTemplateId),
                                voicifyApi.getApplicationEnvironments(independentTarget.applicationId)
                            ]);
                            if (templateResult.resultType !== 'Ok' || environmentsResult.resultType !== 'Ok') {
                                return { ...independentTarget, error: `Unable to load the independent ${moduleName} module or application environments.` };
                            }

                            const environments = environmentsResult.data || [];
                            const primaryEnvironment = environments.find(environment => !environment.isCustom && !environment.isDraft)
                                || environments.find(environment => !environment.isCustom && environment.isDraft);
                            if (!primaryEnvironment?.id) {
                                return { ...independentTarget, error: 'No production or draft environment was found.' };
                            }

                            return {
                                ...independentTarget,
                                appliedTemplate: templateResult.data,
                                publishToEnvironmentId: primaryEnvironment.id
                            };
                        });

                        const independentModuleRequests: Array<{
                            applicationId: string;
                            appliedTemplateId: string;
                            appliedTemplate: AppliedApplicationTemplateModel;
                            publishToEnvironmentId: string;
                        }> = [];
                        independentModulePreflightResults.forEach(request => {
                            if ('error' in request) {
                                errors.push(`App ${request.applicationId}: ${request.error}`);
                            } else {
                                independentModuleRequests.push(request);
                            }
                        });

                        if (errors.length === 0 && (sharedConfigurationApplicationIds.length > 0
                            || (independentModuleRequests.length === 0 && publishOnlyApplicationIds.length > 0))) {
                            const syncResult = await voicifyApi.queueManagedModuleSyncBatch({
                                organizationId: target.id,
                                applicationIds: sharedConfigurationApplicationIds,
                                sharedTopicsApplicationId: sharedConfiguration.id,
                                publishAfterSync: sharedConfigurationApplicationIds.length > 0
                            });

                            if (syncResult.resultType === 'Ok') {
                                acceptedJobCount += syncResult.data.acceptedJobs.length;
                                errors.push(...syncResult.data.validationFailures.map(failure => failure.error));
                            } else {
                                errors.push(...(syncResult.errors?.length ? syncResult.errors : ['Unable to queue shared sync and publish work.']));
                            }
                        }

                        if (errors.length === 0) {
                            // The existing sync endpoint creates the managed module-sync -> backup -> publish
                            // dependency chain when a publish environment is supplied.
                            const independentSyncResults = await mapWithConcurrency(independentModuleRequests, 5, request =>
                                voicifyApi.queueUpdateApplicationFromTemplateFormsSync(
                                    request.applicationId,
                                    request.appliedTemplateId,
                                    {
                                        templateFormFieldValues: request.appliedTemplate.templateFormFieldValues || [],
                                        dynamicFormSections: request.appliedTemplate.dynamicFormSections || [],
                                        name: request.appliedTemplate.name,
                                        publishToEnvironmentId: request.publishToEnvironmentId
                                    }
                                )
                            );

                            independentSyncResults.forEach((syncResult, index) => {
                                if (syncResult.resultType === 'Ok' && !syncResult.data?.error) {
                                    acceptedJobCount += 3;
                                } else {
                                    const resultErrors = syncResult.resultType === 'Ok'
                                        ? [syncResult.data?.error || 'Unable to queue independent sync and publish work.']
                                        : (syncResult.errors?.length ? syncResult.errors : ['Unable to queue independent sync and publish work.']);
                                    errors.push(...resultErrors.map(error => `App ${independentModuleRequests[index].applicationId}: ${error}`));
                                }
                            });
                        }

                        if (errors.length === 0 && publishOnlyApplicationIds.length > 0) {
                            const publishResult = await voicifyApi.queueManagedBackupPublishBatch({
                                organizationId: target.id,
                                applicationIds: publishOnlyApplicationIds
                            });

                            if (publishResult.resultType === 'Ok') {
                                acceptedJobCount += publishResult.data.acceptedJobs.length;
                                errors.push(...publishResult.data.validationFailures.map(failure => failure.error));
                            } else {
                                errors.push(...(publishResult.errors?.length ? publishResult.errors : ['Unable to queue publish work.']));
                            }
                        }
                    }
                }
            }
        } catch (error) {
            errors.push((error as Error)?.message || 'An unexpected error occurred while queuing this organization.');
        }

        const result = {
            organizationId: target.id,
            organizationName: target.name,
            applicationCount: target.applicationIds.length,
            acceptedJobCount,
            errors
        };
        results.push(result);
        onResult?.(result);
    }

    return results;
};
